import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import {
  Card, LineChart, SectionHead, StatsCards,
} from '../../PipelineComponents';
import { formattedCount } from '../../../pipelineHelpers';
import ShortSupplyMembersCard from './ShortSupplyMembersCard';

const MIX_TIPS = {
  '90-day': {
    text: 'Fills dispensed for a standard 90-day supply.',
    steps: ['Counted as a long supply everywhere else on the dashboard.'],
    note: 'The target state. A member on 90 days has four chances a year to lapse instead of twelve.',
  },
  '60-day': {
    text: 'Fills covering two months.',
    steps: ['Still counted as a short supply.'],
    note: 'The easiest group to convert - these members are already comfortable with a longer supply and only'
      + ' need 30 more days added.',
  },
  '30-day': {
    text: 'Fills covering a month.',
    steps: ['Normally the largest of the short bands.'],
    note: 'The conversion opportunity, but it mixes long-term medications stuck on monthly refills with'
      + ' genuine short courses. Only the first should move.',
  },
  '100-day': {
    text: 'Fills dispensed for 100 days.',
    steps: ['Allowed by some plans only.'],
    note: 'Counts as a long supply alongside 90-day everywhere else on the dashboard.',
  },
};

const ShortSupply = ({ section, params }) => {
  const firstLoad = useRef(true);

  const [shortSupplyData, setShortSupplyData] = useState({});
  const [loading, loadingToggle] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        loadingToggle(true);

        const { data } = await Api.getShortSupplySummary(params);

        setShortSupplyData(data?.data || {});

        loadingToggle(false);
        firstLoad.current = false;
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [params]);

  const cards = useMemo(() => {
    const stats = shortSupplyData?.kpis;

    return [
      {
        label: 'Fills in period',
        value: formattedCount(stats?.fillsInPeriod),
        tooltip: {
          text: 'Every prescription dispensed in the selected dates.',
          steps: ['Counts fills, so one member with six refills contributes six.'],
          note: 'Fills, not members. This is the base for the share below, and it is the number that moves'
            + ' when the date range changes.',
        },
      },
      {
        label: 'Fills under 90 days',
        value: formattedCount(stats?.fillsUnder90),
        tooltip: {
          text: 'Prescriptions dispensed for less than 90 days at a time.',
          steps: ['Anything from a short course up to an 89-day supply.'],
          note: 'Not all of these should be 90 days — antibiotics and new starts belong on a short supply.'
            + ' The table beside this narrows it to the ones worth converting.',
        },
      },
      {
        label: 'Share under 90 days',
        value: stats?.fillsUnder90Pct ? `${stats.fillsUnder90Pct}%` : '0',
        tooltip: {
          text: 'How much of your dispensing is on a short supply.',
          steps: ['Short fills divided by every fill in the period.'],
          note: 'Every short fill is another chance for a member to fall behind. Bringing this down is the'
            + ' single biggest lever on the gap days in section 01.',
        },
      },
      {
        label: 'Members on <90 supply',
        value: formattedCount(stats?.membersOnShortSupply),
        tooltip: {
          text: 'How many different members are picking up short supplies.',
          steps: ['Each member counted once, however many short fills they have.'],
          note: 'Much smaller than the fills count above, because the same members come back repeatedly.'
            + ' That is what makes this a workable call list rather than a wall of transactions.',
        },
      },
      {
        label: 'Avg days supply',
        value: stats?.avgDaysSupply,
        tooltip: {
          text: 'The average length of a prescription across all fills in the period.',
          steps: ['Averaged over every fill, short and long together.'],
          note: 'How close this sits to 90 is the whole story of this section — it says how far the book still'
            + ' has to go towards a long-supply model.',
        },
      },
      {
        label: '100-day fills',
        value: formattedCount(stats?.hundredDayFills),
        tooltip: {
          text: 'Prescriptions dispensed for 100 days, which some plans allow.',
          steps: ['Counted as a long supply alongside 90 days everywhere else on the dashboard.'],
          note: 'A member moving to 100 days counts exactly the same as one moving to 90.',
        },
      },
    ];
  }, [shortSupplyData]);

  const mix = useMemo(() => (shortSupplyData?.supplyMix || []).map((d) => ({
    label: d.bucket,
    value: d.count || 0,
    caption: d.pct ? `${d.count} · ${d.pct}%` : d.count,
    tooltip: MIX_TIPS[d.bucket],
  })), [shortSupplyData]);

  return (
    <>
      <SectionHead section={section} />

      <StatsCards
        firstLoad={firstLoad.current}
        loading={loading}
        cards={cards}
      />

      <div className={`${classes.row} ${classes.row_2u}`}>
        <ShortSupplyMembersCard params={params} />

        <Card
          title="Days-supply mix"
          caption="Fills in the selected period"
          loading={loading}
          firstLoad={firstLoad.current}
          isEmpty={!mix.length}
        >
          <LineChart data={mix} />
        </Card>
      </div>
    </>
  );
};

ShortSupply.propTypes = {
  section: PropTypes.object.isRequired,
  params: PropTypes.object.isRequired,
};

export default ShortSupply;
